import { useEffect, useState, useRef } from "react";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, UploadSimple, FilePdf } from "@phosphor-icons/react";

const CLASSES = [
  ["individual", "Individual / Sole proprietor"],
  ["c_corp", "C Corporation"],
  ["s_corp", "S Corporation"],
  ["partnership", "Partnership"],
  ["llc", "LLC"],
  ["trust", "Trust / Estate"],
  ["other", "Other"],
];

const EMPTY = {
  legal_name: "", business_name: "", tax_classification: "individual",
  address_line1: "", city: "", state: "", zip: "", tin_type: "SSN",
};

export function W9Form({ base, defaultName = "", onSaved }) {
  const [w9, setW9] = useState(undefined);
  const [f, setF] = useState({ ...EMPTY, legal_name: defaultName });
  const [tin, setTin] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`${base}/w9`).then((r) => setW9(r.data)).catch(() => setW9({ on_file: false }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);

  useEffect(() => {
    if (w9 && w9.on_file) {
      setF({
        legal_name: w9.legal_name || "", business_name: w9.business_name || "",
        tax_classification: w9.tax_classification || "individual",
        address_line1: w9.address_line1 || "", city: w9.city || "",
        state: w9.state || "", zip: w9.zip || "", tin_type: w9.tin_type || "SSN",
      });
    } else if (w9) {
      setF((p) => ({ ...p, legal_name: p.legal_name || defaultName }));
    }
  }, [w9, defaultName]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    const digits = (tin || "").replace(/\D/g, "");
    if (!f.legal_name || !f.address_line1 || !f.city || !f.state || !f.zip) { toast.error("Fill name and full address"); return; }
    if (digits.length !== 9) { toast.error("Enter the 9-digit TIN (SSN or EIN) to save"); return; }
    setBusy(true);
    try {
      await api.post(`${base}/w9`, { ...f, tin: digits });
      toast.success("W-9 saved — TIN encrypted at rest");
      setTin("");
      load(); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`${base}/w9/document`, fd);
      toast.success("Signed W-9 PDF stored (private)");
      load(); onSaved && onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  if (w9 === undefined) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="w9-form">
      {w9.on_file && (
        <div data-testid="w9-on-file" className="flex flex-wrap items-center gap-3 rounded-sm border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 p-3">
          <CheckCircle size={18} weight="fill" className="text-[hsl(var(--success))]" />
          <span className="text-sm">W-9 on file · {w9.tin_type} <span data-testid="w9-tin-masked" className="font-mono">{w9.tin_masked}</span></span>
          {w9.has_document && (
            <button data-testid="w9-doc-view" onClick={() => window.open(`${API}${base}/w9/document`, "_blank")}
              className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline"><FilePdf size={14} /> View signed PDF</button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5"><Label className="overline">Legal name (as on tax return)</Label>
          <Input data-testid="w9-legal-name" value={f.legal_name} onChange={set("legal_name")} placeholder="Jane Q. Doe" /></div>
        <div className="col-span-2 space-y-1.5"><Label className="overline">Business name (optional)</Label>
          <Input data-testid="w9-business-name" value={f.business_name} onChange={set("business_name")} placeholder="DBA / entity" /></div>
        <div className="col-span-2 space-y-1.5"><Label className="overline">Federal tax classification</Label>
          <Select value={f.tax_classification} onValueChange={(v) => setF({ ...f, tax_classification: v })}>
            <SelectTrigger data-testid="w9-classification"><SelectValue /></SelectTrigger>
            <SelectContent>{CLASSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="col-span-2 space-y-1.5"><Label className="overline">Address</Label>
          <Input data-testid="w9-address" value={f.address_line1} onChange={set("address_line1")} placeholder="123 Main St" /></div>
        <div className="space-y-1.5"><Label className="overline">City</Label>
          <Input data-testid="w9-city" value={f.city} onChange={set("city")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="overline">State</Label>
            <Input data-testid="w9-state" value={f.state} onChange={set("state")} placeholder="NY" /></div>
          <div className="space-y-1.5"><Label className="overline">ZIP</Label>
            <Input data-testid="w9-zip" value={f.zip} onChange={set("zip")} className="font-mono" /></div>
        </div>
        <div className="space-y-1.5"><Label className="overline">TIN type</Label>
          <Select value={f.tin_type} onValueChange={(v) => setF({ ...f, tin_type: v })}>
            <SelectTrigger data-testid="w9-tin-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="SSN">SSN</SelectItem><SelectItem value="EIN">EIN</SelectItem></SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label className="overline">{w9.on_file ? "Re-enter TIN to save" : "TIN (9 digits)"}</Label>
          <Input data-testid="w9-tin" value={tin} onChange={(e) => setTin(e.target.value)} placeholder={f.tin_type === "EIN" ? "12-3456789" : "123-45-6789"} className="font-mono" /></div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="w9-save-btn" onClick={submit} disabled={busy}>{busy ? "Saving…" : w9.on_file ? "Update W-9" : "Save W-9"}</Button>
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="w9-doc-input" />
        <Button variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} data-testid="w9-doc-upload-btn"><UploadSimple size={16} /> Upload signed W-9 (PDF)</Button>
      </div>
    </div>
  );
}
